import { useState, useEffect } from "react";
import { getUnregisteredIssuersGrid } from "../api/NonIntegratedCfdiAPI";
import translate from "../i18n/Translator";
import {
    UnregisteredIssuersGridResponse,
    UnloadedInvoicesFilterRequest
} from "../model/CfdisNonIntegrated";

interface UseUnregisteredIssuersGridResult {
    data: UnregisteredIssuersGridResponse | null;
    loading: boolean;
    error: string | null;
    refetch: () => void;
}

export function useUnregisteredIssuersGrid(
    tenantId: string,
    filters: UnloadedInvoicesFilterRequest | null,
    page: number = 0,
    pageSize: number = 20
): UseUnregisteredIssuersGridResult {
    const [data, setData] = useState<UnregisteredIssuersGridResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const fetchData = async () => {
        if (!tenantId || 
            !filters || 
            !filters.paymentMethod || 
            !filters.startDate || 
            !filters.endDate ||
            filters.paymentMethod.trim() === '' ||
            isNaN(filters.startDate.getTime()) ||
            isNaN(filters.endDate.getTime())) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await getUnregisteredIssuersGrid(tenantId, filters, page, pageSize);
            setData(response);
        } catch (err) {
            setError(err.message || translate("cfdis.cfdis_non_integrated.messages.error_unregistered_grid"));
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [
        tenantId, 
        filters?.paymentMethod, 
        filters?.startDate?.getTime(), 
        filters?.endDate?.getTime(),
        page,
        pageSize
    ]);

    const refetch = () => {
        fetchData();
    };

    return {
        data,
        loading,
        error,
        refetch
    };
}